import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CART_KEY = "pos_user_cart";

// Entirely frontend-local — no backend cart API exists (confirmed by
// inspecting every router). Works identically for guest and logged-in
// customer, persists across refresh via localStorage. Only display data
// is stored here (name/image/unit/price/stock as last seen) — the
// backend remains the source of truth for the real price/stock/total at
// order-request time; nothing here is trusted for that.
//
// Line identity is `product_id` + `unit_code` (not product_id alone) —
// the same product selected with two different units (e.g. Can vs Case)
// must be two separate cart lines, each with its own unit-specific price.
const CartContext = createContext(null);

function lineId(productId, unitCode) {
  return `${productId}::${unitCode || "_base"}`;
}

function readInitialCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readInitialCart);

  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
      // localStorage can throw (private mode, quota) — cart just stays in-memory for this tab
    }
  }, [items]);

  // `unit` is a real ProductUnitOut row ({unit_code, selling_price,
  // conversion_factor, is_base_unit, ...}) selected on the Product Card,
  // or null when the product has no configured units at all (falls back
  // to the product's own flat unit/price fields).
  const addItem = useCallback((product, unit, qty = 1) => {
    const unitCode = unit?.unit_code ?? product.unit ?? null;
    const price = unit ? unit.selling_price : product.price;
    const id = lineId(product.id, unitCode);
    const stock = product.stock ?? null; // global stock — a coarse client-side cap only; the backend validates for real

    setItems((prev) => {
      const existing = prev.find((i) => i.id === id);
      if (existing) {
        const nextQty = stock != null ? Math.min(existing.qty + qty, stock) : existing.qty + qty;
        return prev.map((i) => (i.id === id ? { ...i, qty: nextQty } : i));
      }
      const initialQty = stock != null ? Math.min(qty, Math.max(stock, 0)) : qty;
      return [
        ...prev,
        {
          id,
          product_id: product.id,
          unit_code: unitCode,
          name: product.name,
          image: product.image || null,
          price,
          currency: product.currency || "USD",
          stock,
          qty: initialQty,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.id !== id) return i;
          const clamped = i.stock != null ? Math.min(Math.max(qty, 0), Math.max(i.stock, 0)) : Math.max(qty, 0);
          return { ...i, qty: clamped };
        })
        .filter((i) => i.qty > 0)
    );
  }, []);

  const incQty = useCallback(
    (id) => {
      const item = items.find((i) => i.id === id);
      if (item) setQty(id, item.qty + 1);
    },
    [items, setQty]
  );

  const decQty = useCallback(
    (id) => {
      const item = items.find((i) => i.id === id);
      if (item) setQty(id, item.qty - 1);
    },
    [items, setQty]
  );

  const clear = useCallback(() => setItems([]), []);

  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.qty, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, itemCount, addItem, removeItem, setQty, incQty, decQty, clear }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
